import { BaseNylasConnectorInterface } from '@/connector/connector-interface';
import { debug, error } from '@/utils/utils';
import { ComponentInterface, getElement } from '@stencil/core';
import { CombinedStoreStateKeys, EventEmitterEventType, ExtractEventEmitterKeys, ExtractEventEmitterProperties } from './types';
import { HTMLStencilElement } from '@stencil/core/internal';
export interface RegisteredComponent<H extends ComponentInterface, B extends BaseNylasConnectorInterface, MailboxStores = string> {
  name: string;
  element: HTMLStencilElement & { setProperty?: (key: string, value: any) => Promise<void>; registerCallback?(): Promise<void> };
  getStoresToProp?: keyof H;
  storeToProps?: Map<string, keyof H>;
  stateToProps?: Map<MailboxStores, keyof H>;
  eventToProps?: Map<ExtractEventEmitterKeys<H>, (event: CustomEvent<EventEmitterEventType<H[ExtractEventEmitterKeys<H>]>>, nylasConnector: B) => Promise<void>>;
  localPropsToProp?: Map<string, keyof H>;
  authToProp?: keyof H;
  connectorToProp?: keyof H;
  registrationEventName?: string;
  unregistrationEventName?: string;
}

export function createComponentEvent<T extends ComponentInterface, B extends BaseNylasConnectorInterface>(
  instance: T,
  registeredComponent: RegisteredComponent<T, B>,
  eventName: string = 'registerComponent',
) {
  return new CustomEvent<RegisteredComponent<typeof instance, B>>(eventName, {
    bubbles: true,
    composed: true,
    detail: registeredComponent,
  });
}

export function unregisterComponentEvent<T extends ComponentInterface, B extends BaseNylasConnectorInterface>(
  instance: T,
  registeredComponent: RegisteredComponent<T, B>,
  eventName: string = 'unregisterComponent',
) {
  return new CustomEvent<RegisteredComponent<typeof instance, B>>(eventName, {
    bubbles: true,
    composed: true,
    detail: registeredComponent,
  });
}

export interface RegisterComponentConfig<H extends ComponentInterface, B extends BaseNylasConnectorInterface, MailboxStores>
  extends Omit<RegisteredComponent<H, B, MailboxStores>, 'eventToProps' | 'name' | 'element'> {
  name: string;
  eventToProps?: Partial<{
    [P in ExtractEventEmitterProperties<H> as P['key']]: (event: CustomEvent<P['eventType']>, nylasConnector: B) => Promise<void>;
  }>;
  fireRegisterEvent?: boolean | ((host: H) => boolean);
}

export interface NylasComponentInterface extends ComponentInterface {
  authToProp?: keyof this;
  connectorToProp?: keyof this;
  registerCallback?: () => Promise<void>;
}

export function RegisterComponent<H extends NylasComponentInterface, B extends BaseNylasConnectorInterface, MailboxStores extends Record<string, any>>(
  config: RegisterComponentConfig<H, B, CombinedStoreStateKeys<keyof MailboxStores, MailboxStores>>,
) {
  return (target: H, propKey: string) => {
    const componentName = config.name;
    debug(`[${componentName}] Registering component ${componentName} being called on ${propKey}`);

    const originalComponentWillLoad = target.componentWillLoad;
    if (!originalComponentWillLoad) {
      error(`[${componentName}] componentWillLoad lifecycle method in ${componentName} missing. Required for RegisterComponent decorator.`);
      return;
    }

    const originalConnectCallback = target.connectedCallback;
    if (!originalConnectCallback) {
      error(`[${componentName}] connectedCallback lifecycle method in ${componentName} missing. Required for RegisterComponent decorator.`);
      return;
    }

    const originalDisconnectCallback = target.disconnectedCallback;
    if (!originalDisconnectCallback) {
      error(`[${componentName}] disconnectedCallback lifecycle method in ${componentName} missing. Required for RegisterComponent decorator.`);
      return;
    }

    // Detect a conflict if config.storeToProps and config.stateToProps are both defined
    // for overlapping prop keys
    if (config.storeToProps && config.stateToProps) {
      const storeToPropsKeys = Array.from(config.storeToProps.values());
      const stateToPropsKeys = Array.from(config.stateToProps.values());
      const intersection = storeToPropsKeys.filter(key => stateToPropsKeys.includes(key));
      if (intersection.length > 0) {
        error(
          `[${componentName}] Overlapping prop keys detected in ${componentName} for storeToProps and stateToProps. Make sure to only define them once. Overlapping keys: ${intersection.join(
            ', ',
          )}`,
        );
        return;
      }
    }

    // Convert the record of eventToProps to a map
    const eventToPropsMap = new Map<ExtractEventEmitterKeys<H>, (event: CustomEvent<EventEmitterEventType<H[ExtractEventEmitterKeys<H>]>>, nylasConnector: B) => Promise<void>>();
    if (config.eventToProps) {
      Object.keys(config.eventToProps).forEach(key => {
        eventToPropsMap.set(
          key as ExtractEventEmitterKeys<H>,
          config.eventToProps?.[key as ExtractEventEmitterKeys<H>] as (
            event: CustomEvent<EventEmitterEventType<H[ExtractEventEmitterKeys<H>]>>,
            nylasConnector: B,
          ) => Promise<void>,
        );
      });
    }

    target.connectedCallback = function () {
      debug(`[~${componentName}] connectedCallback called`);
      const origionalResult = originalConnectCallback?.call(this);
      const host = getElement(this);

      // If the nylas id is already set, ensure properties are still set up
      // This handles the case where component was disconnected and reconnected
      // Properties may have been cleared during disconnect
      if (host.dataset.nylasId) {
        // Still set up properties in case they were cleared during disconnect
        // This ensures reconnected components have proper setup
        Object.defineProperty(host, 'registerNylasComponent', {
          get() {
            return true;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'stateToProps', {
          get() {
            return config.stateToProps;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'getStoresToProp', {
          get() {
            return config.getStoresToProp;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'storeToProps', {
          get() {
            return config.storeToProps;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'eventToProps', {
          get() {
            return eventToPropsMap;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'authToProp', {
          get() {
            return config.authToProp;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'connectorToProp', {
          get() {
            return config.connectorToProp;
          },
          enumerable: true,
        });
        Object.defineProperty(host, 'localPropsToProp', {
          get() {
            return config.localPropsToProp;
          },
          enumerable: true,
        });
        return origionalResult;
      }

      Object.defineProperty(host, 'registerNylasComponent', {
        get() {
          return true;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'stateToProps', {
        get() {
          return config.stateToProps;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'getStoresToProp', {
        get() {
          return config.getStoresToProp;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'storeToProps', {
        get() {
          return config.storeToProps;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'eventToProps', {
        get() {
          return eventToPropsMap;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'authToProp', {
        get() {
          return config.authToProp;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'connectorToProp', {
        get() {
          return config.connectorToProp;
        },
        enumerable: true,
      });
      Object.defineProperty(host, 'localPropsToProp', {
        get() {
          return config.localPropsToProp;
        },
        enumerable: true,
      });
      return origionalResult;
    };

    if (config.fireRegisterEvent) {
      target.componentWillLoad = function () {
        const origionalResult = originalComponentWillLoad?.call(this);
        const host = getElement(this);

        // We use our host element to dispatch a registration event that is picked up by the
        // the nylas-provider component.
        const registeredComponent: RegisteredComponent<H, B> = {
          element: host,
          ...config,
          eventToProps: eventToPropsMap,
        };
        const event = createComponentEvent(target, registeredComponent, config.registrationEventName);
        if (host.dispatchEvent(event)) {
          debug(`[${componentName}] Component ${componentName} successfully fired registration event`);
        }

        return origionalResult;
      };

      target.disconnectedCallback = function () {
        const origionalResult = originalDisconnectCallback?.call(this);
        const host = getElement(this);

        // We use our host element to dispatch a registration event that is picked up by the
        // the nylas-provider component.
        const registeredComponent: RegisteredComponent<H, B> = {
          element: host,
          ...config,
          eventToProps: eventToPropsMap,
        };
        const event = unregisterComponentEvent(target, registeredComponent, config.unregistrationEventName);

        // Dispatch event before clearing nylasId to ensure it bubbles to provider
        // disconnectedCallback is called before element is fully detached, so event should bubble
        try {
          if (host.dispatchEvent(event)) {
            debug(`[${componentName}] Component ${componentName} successfully fired unregistration event`);
          }
        } catch (error) {
          debug(`[${componentName}] Error dispatching unregistration event:`, error);
        }

        // Clear nylasId on disconnect so reconnection triggers fresh registration
        // This ensures reconnected components get proper property setup
        if (host.dataset.nylasId) {
          delete host.dataset.nylasId;
          debug(`[${componentName}] Cleared nylasId on disconnect for component ${componentName}`);
        }

        return origionalResult;
      };
    }
  };
}
